import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Avatar,
  AppBar,
  Box,
  Chip,
  Divider,
  Drawer,
  GlobalStyles,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DnsIcon from '@mui/icons-material/Dns';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShieldIcon from '@mui/icons-material/Shield';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TuneIcon from '@mui/icons-material/Tune';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import { useAuth } from '../hooks/useAuth';
import { useAppTheme } from '../contexts/ThemeContext';

const DRAWER_WIDTH = 76;
const ADMIN_LEDGER_CURRENCY_KEY = 'streamy.admin.displayCurrency';
const currencyOptions = ['USD', 'KES', 'EUR', 'GBP', 'TZS', 'UGX', 'ZAR', 'NGN'];

const MENU_ITEMS = [
  { label: 'System Overview', path: '/admin', icon: <DashboardIcon />, description: 'Dashboard & Metrics' },
  { label: 'User Governance', path: '/admin/users', icon: <ManageAccountsIcon />, description: 'Account Management' },
  { label: 'Stream NOC', path: '/admin/streams', icon: <DnsIcon />, description: 'Network Operations' },
  { label: 'NOC Reports', path: '/admin/streams/reports', icon: <QueryStatsIcon />, description: 'Stream operations reports' },
  { label: 'Financial Ledger', path: '/admin/billing', icon: <ReceiptLongIcon />, description: 'Billing & Revenue' },
  { label: 'Financial Reports', path: '/admin/billing/reports', icon: <RequestQuoteIcon />, description: 'Finance strategy reports' },
  { label: 'Reports', path: '/admin/reports', icon: <SummarizeIcon />, description: 'Operational Reports' },
  { label: 'Service Status', path: '/admin/status', icon: <MonitorHeartIcon />, description: 'Go Service Health' },
  { label: 'System Config', path: '/admin/system', icon: <TuneIcon />, description: 'Infrastructure Settings' },
];

export const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [currencyMenuAnchor, setCurrencyMenuAnchor] = useState<null | HTMLElement>(null);
  const [displayCurrency, setDisplayCurrency] = useState(() => localStorage.getItem(ADMIN_LEDGER_CURRENCY_KEY) || user?.currency || 'USD');
  const logoSrc = mode === 'dark'
    ? '/streamy-logo-navy-blue-background-white-text.png'
    : '/streamy-logo-white-background-navy-blue-text.png';
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setProfileMenuAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileMenuAnchor(null);
  const handleCurrencyChange = (currency: string) => {
    setDisplayCurrency(currency);
    localStorage.setItem(ADMIN_LEDGER_CURRENCY_KEY, currency);
    window.dispatchEvent(new CustomEvent('streamy-admin-currency-change', { detail: { currency } }));
    setCurrencyMenuAnchor(null);
  };


  const adminAccent = mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main;
  const themedIconButtonSx = {
    color: adminAccent,
    borderColor: `${alpha(adminAccent, 0.45)} !important`,
    bgcolor: alpha(adminAccent, mode === 'dark' ? 0.08 : 0.05),
    '&:hover': {
      color: adminAccent,
      borderColor: `${adminAccent} !important`,
      bgcolor: alpha(adminAccent, mode === 'dark' ? 0.18 : 0.12),
    },
  } as const;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 1, width: '100%' }}>
        <Tooltip title={`Admin Core · ${user?.role || 'ADMIN'}`} placement="right" arrow>
          <Box sx={{ width: 52, height: 52, mx: 'auto', borderRadius: 2, bgcolor: alpha(adminAccent, 0.1), border: '1px solid', borderColor: alpha(adminAccent, 0.45), display: 'flex', alignItems: 'center', justifyContent: 'center', color: adminAccent }}>
            <ShieldIcon sx={{ fontSize: 28 }} />
          </Box>
        </Tooltip>
      </Box>
      <Divider flexItem />
      <List sx={{ px: 1, py: 1.25, flexGrow: 1, width: '100%' }}>
        {MENU_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Tooltip key={item.label} title={`${item.label} · ${item.description}`} placement="right" arrow>
              <ListItemButton
                selected={isActive}
                aria-label={item.label}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  mb: 0.65,
                  minHeight: 44,
                  px: 1,
                  justifyContent: 'center',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isActive ? adminAccent : theme.palette.divider,
                  bgcolor: isActive ? alpha(adminAccent, mode === 'dark' ? 0.16 : 0.1) : 'transparent',
                  color: isActive ? adminAccent : 'text.secondary',
                  '&.Mui-selected': {
                    bgcolor: alpha(adminAccent, mode === 'dark' ? 0.16 : 0.1),
                    color: adminAccent,
                    '&:hover': { bgcolor: alpha(adminAccent, mode === 'dark' ? 0.22 : 0.14) },
                  },
                  '&:hover': {
                    borderColor: adminAccent,
                    bgcolor: alpha(adminAccent, mode === 'dark' ? 0.12 : 0.08),
                    color: adminAccent,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'inherit', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
      <Divider flexItem />
      <Box sx={{ p: 1, width: '100%' }}>
        <Tooltip title={`${user?.firstName || 'Admin'} ${user?.lastName || ''} · ${user?.role || 'ADMIN'}`} placement="right" arrow>
          <Avatar sx={{ mx: 'auto', mb: 1, bgcolor: alpha(adminAccent, 0.14), color: adminAccent, border: '1px solid', borderColor: alpha(adminAccent, 0.4), width: 42, height: 42, borderRadius: 2, fontWeight: 800 }}>{user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}</Avatar>
        </Tooltip>
        <Tooltip title="Secure Logout" placement="right" arrow>
          <ListItemButton aria-label="Secure Logout" onClick={logout} sx={{ minHeight: 42, justifyContent: 'center', px: 1, borderRadius: 2, color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.06), border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.25), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12), borderColor: theme.palette.error.main } }}>
            <ListItemIcon sx={{ minWidth: 0, color: 'inherit', justifyContent: 'center' }}><ExitToAppIcon /></ListItemIcon>
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box
      className="streamy-admin-shell"
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        '& .MuiPaper-root, & .MuiCard-root, & .MuiButton-root, & .MuiIconButton-root, & .MuiChip-root, & .MuiOutlinedInput-root, & .MuiMenu-paper, & .MuiDrawer-paper, & .MuiAlert-root, & .MuiPaginationItem-root, & .MuiTableContainer-root': {
          borderRadius: '8px !important',
        },
        '& .MuiIconButton-root:not(.MuiIconButton-colorError):not(.MuiIconButton-colorSuccess):not(.MuiIconButton-colorWarning)': themedIconButtonSx,
        '& .MuiAvatar-root': { borderRadius: '10px !important' },
        '& .MuiLinearProgress-root': { borderRadius: '999px !important' },
      }}
    >
      <GlobalStyles styles={{
        '.MuiDialog-paper, .MuiPopover-paper, .MuiMenu-paper': { borderRadius: '8px !important' },
        '.MuiTooltip-tooltip': { borderRadius: '6px !important' },
      }} />
      <AppBar position="fixed" elevation={0} sx={{ width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: `${DRAWER_WIDTH}px` }, bgcolor: 'background.paper', color: 'text.primary', borderBottom: `2px solid ${theme.palette.divider}`, zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ py: 1 }}>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { md: 'none' }, borderRadius: 0, border: `1px solid ${theme.palette.divider}` }}><MenuIcon /></IconButton>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ width: { xs: 86, sm: 116 }, height: 32, px: 0.75, display: 'flex', alignItems: 'center', bgcolor: mode === 'dark' ? 'background.paper' : '#fff', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
              <Box component="img" src={logoSrc} alt="Streamy" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            </Box>
            <Box sx={{ width: 6, height: 32, bgcolor: theme.palette.error.main, borderRadius: 1, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}><Typography variant="h6" fontWeight={800} noWrap sx={{ letterSpacing: 1, fontSize: '1.1rem' }}>ADMINISTRATION CONSOLE</Typography><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>Secure Management Interface</Typography></Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center"><Tooltip title="Admin display currency"><Chip label={displayCurrency} onClick={(event) => setCurrencyMenuAnchor(event.currentTarget)} size="small" sx={{ borderRadius: 0, fontWeight: 900, border: `1px solid ${theme.palette.divider}`, display: { xs: 'none', sm: 'inline-flex' } }} /></Tooltip><Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} theme`}><IconButton onClick={toggleColorMode} sx={{ borderRadius: 0, border: `1px solid ${theme.palette.divider}`, '&:hover': { borderColor: theme.palette.primary.main } }}>{mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}</IconButton></Tooltip><Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} /><Stack direction="row" spacing={1.5} alignItems="center" onClick={handleProfileMenuOpen} sx={{ cursor: 'pointer', p: 1, borderRadius: 0, transition: 'all 0.2s', border: `1px solid transparent`, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05), borderColor: theme.palette.primary.main } }}><Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>{user?.firstName} {user?.lastName}</Typography><Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end"><AdminPanelSettingsIcon sx={{ fontSize: 12, color: 'error.main' }} /><Typography variant="caption" color="error.main" fontWeight={600} sx={{ fontSize: '0.7rem' }}>{user?.role || 'ADMIN'}</Typography></Stack></Box><Avatar sx={{ bgcolor: theme.palette.error.main, borderRadius: 0, width: 40, height: 40, fontWeight: 700, boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}` }}>{user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}</Avatar><KeyboardArrowDownIcon fontSize="small" /></Stack></Stack>
        </Toolbar>
      </AppBar>
      <Menu anchorEl={currencyMenuAnchor} open={Boolean(currencyMenuAnchor)} onClose={() => setCurrencyMenuAnchor(null)} PaperProps={{ sx: { borderRadius: 0, border: `1px solid ${theme.palette.divider}` } }}>{currencyOptions.map((currency) => <MenuItem key={currency} selected={currency === displayCurrency} onClick={() => handleCurrencyChange(currency)}>{currency}</MenuItem>)}</Menu>
      <Menu anchorEl={profileMenuAnchor} open={Boolean(profileMenuAnchor)} onClose={handleProfileMenuClose} PaperProps={{ sx: { borderRadius: 0, border: `1px solid ${theme.palette.divider}`, minWidth: 220, mt: 1 } }}><Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}><Typography variant="body2" fontWeight={700}>{user?.email}</Typography><Typography variant="caption" color="text.secondary">{user?.role}</Typography></Box><MenuItem onClick={() => { handleProfileMenuClose(); logout(); }} sx={{ py: 1.5, color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}><ListItemIcon><ExitToAppIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Logout</ListItemText></MenuItem></Menu>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}><Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: `1px solid ${theme.palette.divider}` } }}>{drawerContent}</Drawer><Drawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: `2px solid ${theme.palette.divider}` } }} open>{drawerContent}</Drawer></Box>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, mt: { xs: 9, sm: 10 }, minHeight: 'calc(100vh - 80px)' }}><Outlet /></Box>
    </Box>
  );
};
